
export const DOC_PREFIX_MAP = {
  contract: "CT",
  case: "CASE",
  dispute: "DIS",
  ip: "IP",
  brand: "BA",
  lawyer: "LEG",
  governance: "GOV",
  policy: "POL", // New: Official Policy
  gov_in: "IN",
  gov_out: "OUT",
  seal: "SEAL",
  sign: "SIGN",
  task: "TSK",
  template: "TMP",
  gov_project: "GOVP",
  contract_legacy: "CT-OLD"
} as const;

export type DocPrefixKey = keyof typeof DOC_PREFIX_MAP;

export function formatDatePoints(dateInput?: string | Date, monthlyOnly: boolean = false): string {
  const d = dateInput ? new Date(dateInput) : new Date();
  if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 10).replace(/-/g, ""); // fallback today
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return monthlyOnly ? `${y}${m}` : `${y}${m}${day}`;
}

/**
 * Standardized Document Code Generator
 * Format: PREFIX-YYYYMMDD-SEQ (e.g., SEAL-20251201-001)
 * Or: PREFIX-YYYYMM-SEQ (if monthlyOnly is true)
 */
export function generateDocCode(
  prefixKey: DocPrefixKey | string, 
  dateInput: string | Date = new Date(), 
  existingItems: any[] = [],
  monthlyOnly: boolean = false
): string {
  // 1. Determine Prefix
  let prefix = (DOC_PREFIX_MAP as any)[prefixKey] || prefixKey || "DOC";
  
  // 2. Format Date (YYYYMMDD or YYYYMM)
  const dateStr = formatDatePoints(dateInput, monthlyOnly);
  
  // 3. Construct Base Pattern
  // Example: SEAL-20251203- or POL-202512-
  const basePattern = `${prefix}-${dateStr}-`;
  
  // 4. Find Max Sequence in Existing Items
  let maxSeq = 0;
  
  existingItems.forEach(item => {
    // Check various common ID fields
    const code = item.docCode || item.contractNo || item.matterNo || item.caseNo || item.disputeNo || item.ipNo || item.docNo || item.refNo || item.code || item.internalDocNo || "";
    
    if (code && typeof code === 'string' && code.startsWith(basePattern)) {
      const suffix = code.replace(basePattern, "");
      const match = suffix.match(/^(\d+)/);
      if (match) {
        const seq = parseInt(match[1], 10);
        if (!isNaN(seq) && seq > maxSeq) {
          maxSeq = seq;
        }
      }
    }
  });

  // 5. Generate Next Sequence
  const nextSeq = String(maxSeq + 1).padStart(3, "0");
  
  return `${basePattern}${nextSeq}`;
}

/**
 * Legacy Contract Number Generator: CT-OLD-XXXX
 */
export function generateLegacyContractNo(existingItems: any[] = []): string {
  const prefix = "CT-OLD-";
  let maxSeq = 0;

  existingItems.forEach(item => {
    const code = item.contractNo || "";
    if (code && typeof code === 'string' && code.startsWith(prefix)) {
      const suffix = code.replace(prefix, "");
      const match = suffix.match(/^(\d+)/);
      if (match) {
        const seq = parseInt(match[1], 10);
        if (!isNaN(seq) && seq > maxSeq) {
          maxSeq = seq;
        }
      }
    }
  });

  const nextSeq = String(maxSeq + 1).padStart(4, "0");
  return `${prefix}${nextSeq}`;
}

// ... Legacy Wrappers (Keep existing below) ...
export function generateContractNo(signDate: string | undefined, contracts: { contractNo: string }[]): string {
  return generateDocCode("contract", signDate, contracts);
}

export function generateLegalMatterNo(type: string, date: string | undefined, items: { matterNo: string }[]): string {
  return generateDocCode(type === "案件" ? "case" : "dispute", date, items);
}

export function generateCaseNo(date: string | undefined, items: any[]): string {
  return generateDocCode("case", date, items);
}

export function generateDisputeNo(date: string | undefined, items: any[]): string {
  return generateDocCode("dispute", date, items);
}
